import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import Container from "@/components/ui/container";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import WhatsAppFloat from "@/components/ui/whatsapp-float";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/ui/breadcrumbs";
import FAQSection from "@/components/ui/faq-section";
import {
  Workflow,
  Zap,
  TrendingUp,
  Clock,
  Users,
  CheckCircle,
  ArrowRight,
  Settings,
  FileText,
  BarChart3,
  Shield,
  MessageCircle,
  Target,
  Cpu,
  Database,
  Smartphone
} from "lucide-react";
import RelatedLinks from "@/components/ui/related-links";
import { AuroraHero } from "@/components/ui/aurora-hero";

const AutomacaoProcessos = () => {
  const processTypes = [
    {
      icon: Users,
      title: "Recursos Humanos",
      description: "Admissão digital, gestão de férias, avaliação de desempenho",
      processes: ["Onboarding", "Solicitação de Férias", "Avaliação 360°", "Desligamento"],
      reduction: "80%"
    },
    {
      icon: TrendingUp,
      title: "Financeiro",
      description: "Compras, fornecedores, reembolsos e aprovações financeiras",
      processes: ["Solicitação de Compras", "Cadastro de Fornecedores", "Reembolso de Despesas", "Aprovação Orçamentária"],
      reduction: "70%"
    },
    {
      icon: Shield,
      title: "Qualidade",
      description: "Não conformidades, auditorias e gestão de documentos",
      processes: ["Controle de NC", "Auditoria Interna", "Gestão de Documentos", "Ações Corretivas"],
      reduction: "60%"
    },
    {
      icon: Settings,
      title: "TI e Suporte",
      description: "Chamados técnicos, mudanças e controle de ativos",
      processes: ["Service Desk", "Controle de Mudanças", "Gestão de Ativos", "Aprovação de Acessos"],
      reduction: "75%"
    }
  ];

  const benefits = [
    {
      icon: Clock,
      title: "Redução de Tempo",
      description: "Diminua em até 80% o tempo gasto em processos manuais",
      metric: "80% menos tempo"
    },
    {
      icon: TrendingUp,
      title: "Aumento de Produtividade",
      description: "Equipes mais produtivas com processos automatizados",
      metric: "50% mais produtivo"
    },
    {
      icon: FileText,
      title: "Eliminação de Papel",
      description: "Processos 100% digitais com assinatura eletrônica",
      metric: "100% paperless"
    },
    {
      icon: BarChart3,
      title: "Visibilidade Total",
      description: "Dashboards em tempo real com indicadores de performance",
      metric: "Real-time insights"
    }
  ];

  const methodology = [
    {
      step: "01",
      title: "Diagnóstico",
      description: "Mapeamos todos os processos atuais e identificamos oportunidades de automação",
      duration: "1-2 semanas"
    },
    {
      step: "02", 
      title: "Prototipação",
      description: "Criamos protótipos funcionais dos processos automatizados para validação",
      duration: "1-2 semanas"
    },
    {
      step: "03",
      title: "Desenvolvimento",
      description: "Implementamos os processos com tecnologia Fluig e integrações necessárias", 
      duration: "2-6 semanas"
    },
    {
      step: "04",
      title: "Implantação",
      description: "Deploy em produção com treinamento da equipe e acompanhamento",
      duration: "1 semana"
    }
  ];

  const features = [
    "Workflow visual e intuitivo",
    "Formulários dinâmicos e responsivos", 
    "Integrações com sistemas existentes",
    "Aprovações via mobile",
    "Relatórios automáticos",
    "Dashboards executivos",
    "Notificações em tempo real",
    "Assinatura digital",
    "Controle de SLA",
    "Auditoria completa"
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEO 
        title="Automação de Processos Empresariais com Fluig"
        description="Automatize processos de RH, Financeiro, Qualidade e TI com Fluig. Reduza 80% do tempo, elimine papel e aumente 50% a produtividade. Diagnóstico gratuito."
        keywords={['automação processos', 'workflow automatico', 'processos digitais', 'automação empresarial', 'fluig workflow', 'bpm automação', 'processos fluig']}
        type="website"
      />
      <Header />
      
      <main>
        {/* Hero Section */}
        <section id="hero">
          <AuroraHero>
            <div className="text-center max-w-5xl mx-auto">
              <Badge variant="outline" className="mb-6 text-fs-cyan-400 border-fs-cyan-400/30">
                <Workflow className="w-4 h-4 mr-2" />
                Automação Empresarial
              </Badge>

              <h1 className="text-5xl md:text-7xl font-bold mb-6">
                Automação de
                <span className="block fs-gradient-text mt-2">
                  Processos Empresariais
                </span>
              </h1>

              <p className="text-xl text-muted-foreground dark:text-white/70 max-w-3xl mx-auto mb-8 leading-relaxed">
                Transforme processos manuais em workflows digitais inteligentes.
                Reduza até 80% do tempo operacional e elimine 100% do papel com nossa expertise em Fluig.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
                <Button
                  size="lg"
                  variant="fs-primary"
                  onClick={() => window.open('https://wa.me/5581981658439?text=Olá! Quero automatizar meus processos.', '_blank')}
                >
                  <MessageCircle className="w-5 h-5 mr-2" />
                  Diagnóstico Gratuito
                </Button>
                <Button
                  size="lg"
                  variant="fs-secondary"
                  onClick={() => document.getElementById('processos')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  Ver Processos
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>

              {/* Key Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                <div className="text-center">
                  <div className="text-3xl font-bold fs-gradient-text mb-2">80%</div>
                  <div className="text-muted-foreground dark:text-white/70 text-sm">Redução de Tempo</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold fs-gradient-text mb-2">100%</div>
                  <div className="text-muted-foreground dark:text-white/70 text-sm">Sem Papel</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold fs-gradient-text mb-2">50%</div>
                  <div className="text-muted-foreground dark:text-white/70 text-sm">Mais Produtividade</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold fs-gradient-text mb-2">30 dias</div>
                  <div className="text-muted-foreground dark:text-white/70 text-sm">Implementação</div>
                </div>
              </div>
            </div>
          </AuroraHero>
        </section>

        {/* Process Types */}
        <section id="processos" className="py-20">
          <Container>
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-bold mb-6 fs-gradient-text">
                  Processos que Automatizamos
                </h2>
                <p className="text-lg text-muted-foreground dark:text-white/70 max-w-2xl mx-auto">
                  Cobrimos todas as áreas da empresa com soluções especializadas
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                {processTypes.map((type, index) => (
                  <Card key={index} className="bg-card/80 backdrop-blur-sm border-border shadow-md dark:shadow-none hover:border-fs-purple-500/40 transition-all duration-300 group">
                    <CardContent className="p-8">
                      <div className="flex items-start justify-between mb-6">
                        <div className="flex items-center">
                          <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-fs-purple-500 to-fs-cyan-500 mr-4">
                            <type.icon className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <h3 className="text-xl font-bold text-foreground dark:text-white group-hover:fs-gradient-text transition-all">
                              {type.title}
                            </h3>
                            <p className="text-muted-foreground dark:text-white/70 text-sm">
                              {type.description}
                            </p>
                          </div>
                        </div>
                        <Badge variant="outline" className="text-fs-cyan-400 border-fs-cyan-400/30">
                          -{type.reduction} tempo
                        </Badge>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        {type.processes.map((process, processIndex) => (
                          <div key={processIndex} className="flex items-center text-muted-foreground dark:text-white/70 text-sm">
                            <CheckCircle className="w-4 h-4 text-fs-cyan-500 mr-2 flex-shrink-0" />
                            {process}
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </Container>
        </section>

        {/* Benefits */}
        <section className="py-24 bg-background dark:bg-fs-dark-950 relative overflow-hidden">
          {/* Background Effects */}
          <div className="absolute inset-0">
            <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-fs-cyan-500/10 rounded-full blur-3xl" />
            <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-fs-magenta-500/10 rounded-full blur-3xl" />
          </div>

          <Container className="py-20 relative z-10">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-bold mb-6 fs-gradient-text">
                  Benefícios da Automação
                </h2>
                <p className="text-lg text-muted-foreground dark:text-white/70 max-w-2xl mx-auto">
                  Resultados mensuráveis que transformam sua operação
                </p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                {benefits.map((benefit, index) => (
                  <Card key={index} className="bg-card/80 backdrop-blur-sm border-border shadow-md dark:shadow-none hover:border-fs-purple-500/40 transition-all duration-300 text-center">
                    <CardContent className="p-6">
                      <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-fs-purple-500 to-fs-cyan-500 mb-4">
                        <benefit.icon className="w-6 h-6 text-white" />
                      </div>
                      <h3 className="text-lg font-semibold text-foreground dark:text-white mb-3">
                        {benefit.title}
                      </h3>
                      <p className="text-muted-foreground dark:text-white/70 text-sm mb-3">
                        {benefit.description}
                      </p>
                      <Badge variant="outline" className="text-fs-cyan-400 border-fs-cyan-400/30">
                        {benefit.metric}
                      </Badge>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </Container>
        </section>

        {/* Methodology */}
        <section className="py-20">
          <Container>
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-bold mb-6 fs-gradient-text">
                  Nossa Metodologia
                </h2>
                <p className="text-lg text-white/70 max-w-2xl mx-auto">
                  Processo estruturado para garantir o sucesso da automação
                </p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                {methodology.map((step, index) => (
                  <Card key={index} className="bg-gradient-to-br from-fs-purple-500/10 to-fs-cyan-500/10 border-fs-purple-500/20">
                    <CardContent className="p-6 text-center">
                      <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gradient-to-br from-fs-purple-500 to-fs-cyan-500 mb-4 text-white font-bold">
                        {step.step}
                      </div>
                      <h3 className="text-lg font-bold text-white mb-3">
                        {step.title}
                      </h3>
                      <p className="text-white/70 text-sm mb-3">
                        {step.description}
                      </p>
                      <Badge variant="outline" className="text-fs-cyan-400 border-fs-cyan-400/30">
                        {step.duration}
                      </Badge>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </Container>
        </section>

        {/* Features */}
        <section className="py-20 bg-gradient-to-b from-fs-section to-fs-section/50">
          <Container>
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-bold mb-6 fs-gradient-text">
                  Recursos Inclusos
                </h2>
                <p className="text-lg text-white/70 max-w-2xl mx-auto">
                  Tudo que você precisa para processos automatizados de sucesso
                </p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-center bg-gray-900/30 rounded-lg p-4 border border-border hover:border-fs-purple-500/40 transition-all">
                    <CheckCircle className="w-5 h-5 text-fs-cyan-500 mr-3 flex-shrink-0" />
                    <span className="text-muted-foreground dark:text-white/70 text-sm">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </Container>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-fs-purple-500/10 via-fs-magenta-500/10 to-fs-cyan-500/10">
          <Container>
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 fs-gradient-text">
                Comece a Automação Hoje
              </h2>
              <p className="text-lg text-white/70 mb-8 max-w-2xl mx-auto">
                Não perca mais tempo com processos manuais. Faça um diagnóstico gratuito 
                e descubra o potencial de automação da sua empresa.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  size="lg" 
                  variant="fs-primary"
                  onClick={() => window.open('https://wa.me/5581981658439?text=Olá! Quero um diagnóstico gratuito de automação.', '_blank')}
                >
                  <MessageCircle className="w-5 h-5 mr-2" />
                  WhatsApp Agora
                </Button>
                <Button 
                  size="lg" 
                  variant="fs-secondary"
                  onClick={() => window.location.href = '/contato'}
                >
                  Solicitar Demonstração
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            </div>
          </Container>
        </section>

        {/* FAQ Section */}
        <section className="py-20">
          <Container>
            <FAQSection 
              title="Perguntas Frequentes - Automação de Processos"
              description="Esclareça suas dúvidas sobre automação de processos empresariais"
              faqs={[
                {
                  question: "Quais processos podem ser automatizados?",
                  answer: "Praticamente qualquer processo que envolva formulários, aprovações e fluxo de trabalho pode ser automatizado: RH (admissão, férias), Financeiro (compras, reembolsos), Qualidade (não conformidades), TI (chamados), Vendas (propostas), entre outros. Analisamos caso a caso."
                },
                {
                  question: "Quanto tempo leva para automatizar um processo?",
                  answer: "Processos simples podem ser automatizados em 1-2 semanas, enquanto processos complexos com múltiplas integrações podem levar 4-8 semanas. Utilizamos metodologia ágil com entregas incrementais a cada 2 semanas."
                },
                {
                  question: "A automação funciona no celular?",
                  answer: "Sim, todos os processos automatizados funcionam perfeitamente em celulares e tablets. Os usuários podem preencher formulários, fazer aprovações e acompanhar status através do app móvel ou navegador mobile."
                },
                {
                  question: "Como funciona a integração com outros sistemas?",
                  answer: "Realizamos integrações nativas com ERPs (TOTVS, SAP, Oracle), sistemas de RH, CRMs, bancos de dados e APIs. As integrações são em tempo real ou por lotes, conforme a necessidade, garantindo sincronização total."
                },
                {
                  question: "Qual o ROI da automação de processos?",
                  answer: "O ROI médio é de 300-500% no primeiro ano. Principais ganhos: redução 70-80% no tempo de processamento, eliminação 100% do papel, redução 60% de erros manuais, aumento 40-50% na produtividade da equipe."
                },
                {
                  question: "Preciso treinar minha equipe?",
                  answer: "Sim, oferecemos treinamento completo incluído no projeto: treinamento de usuários finais, administradores do sistema, documentação técnica e manuais de operação. O treinamento é presencial ou online conforme preferência."
                }
              ]}
            />
          </Container>
        </section>
        {/* Related Links */}
        <section className="py-20 bg-gradient-to-b from-fs-section to-fs-section/50">
          <div className="max-w-6xl mx-auto px-6">
            <RelatedLinks 
              title="Soluções Complementares"
              links={[
                {
                  title: "Consultoria Fluig Especializada",
                  description: "Planejamento estratégico para automação com nossa consultoria e 50.000+ horas de experiência.",
                  href: "/consultoria-fluig",
                  category: "Consultoria"
                },
                {
                  title: "Consultoria em Inteligência Artificial",
                  description: "Combine automação tradicional com IA para processos ainda mais inteligentes e eficientes.",
                  href: "/consultoria-ia",
                  category: "Consultoria"
                },
                {
                  title: "Catálogo FluigApps",
                  description: "Acelere sua automação com nossos 16 FluigApps prontos para diversos processos empresariais.",
                  href: "/flows",
                  category: "Soluções"
                },
                {
                  title: "Serviços de Recursos Humanos",
                  description: "Automatize processos de RH com admissão digital, gestão de férias e workflows integrados.",
                  href: "/servicos/rh",
                  category: "Serviços"
                }
              ]}
            />
          </div>
        </section>
      </main>
      
      <Footer />
      <WhatsAppFloat />
    </div>
  );
};

export default AutomacaoProcessos;